// <s-review-wall> — PEUJE home review wall. Loads the store's curated review file from Shopify Files ([data-src])
// when the band nears the viewport, fills the store-wide summary, then builds two drifting rows of review cards
// (photo cards from the <template data-rw-photo>, quote cards from <template data-rw-quote>) linking to each
// product's reviews. Each row's cards are repeated once (aria-hidden, unfocusable) so the CSS drift loops seamlessly.
// Text goes in with textContent only. One component per file.
class SReviewWall extends HTMLElement {
  #io = null;

  connectedCallback() {
    this.#io = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.#io.disconnect();
      this.#load();
    }, { rootMargin: '600px 0px' });
    this.#io.observe(this);
  }

  disconnectedCallback() {
    this.#io?.disconnect();
  }

  async #load() {
    let data;
    try {
      const response = await fetch(this.dataset.src);
      if (!response.ok) throw new Error(`Review wall ${response.status}`);
      data = await response.json();
    } catch {
      this.hidden = true;
      return;
    }
    if (!data || !Array.isArray(data.reviews) || !data.reviews.length) {
      this.hidden = true;
      return;
    }
    this.#summary(data);
    this.#rows(data);
    this.toggleAttribute('data-ready', true);
  }

  #summary(data) {
    const stars = Array.isArray(data.stars) ? data.stars.map(Number) : [];
    const total = Number(data.total) || stars.reduce((a, b) => a + b, 0);
    const avg = total ? stars.reduce((a, c, i) => a + c * (i + 1), 0) / total : Number(data.rating) || 0;
    const lang = document.documentElement.lang || 'en';
    this.querySelector('[data-rw-avg]').textContent = avg.toFixed(2);
    this.querySelector('[data-rw-fill]').style.width = `${(avg / 5) * 100}%`;
    this.querySelector('[data-rw-stars]').setAttribute('aria-label', this.dataset.ratingAria.replace('[rating]', avg.toFixed(2)));
    this.querySelector('[data-rw-count]').textContent = this.dataset.basedOn.replace('[count]', new Intl.NumberFormat(lang).format(total));
    const top = (stars[4] || 0) + (stars[3] || 0);
    this.querySelector('[data-rw-share]').textContent = total
      ? this.dataset.share.replace('[pct]', String(Math.floor((top / total) * 100)))
      : '';
    this.querySelector('[data-rw-summary]').classList.remove('invisible');
  }

  #rows(data) {
    const base = this.dataset.src.slice(0, this.dataset.src.split('?')[0].lastIndexOf('/') + 1);
    const rows = [[], []];
    data.reviews.forEach((review, index) => rows[index % 2].push(review));
    rows.forEach((reviews, r) => {
      const list = this.querySelector(`[data-rw-row="${r}"]`);
      if (!list) return;
      const cards = reviews.map((review) => this.#card(review, data.products || [], base));
      list.append(...cards);
      for (const card of cards) {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.toggleAttribute('data-rw-clone', true);
        clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
        list.append(clone);
      }
      list.style.setProperty('--rw-duration', `${Math.max(40, reviews.length * 7)}s`);
    });
  }

  #card(review, products, base) {
    const template = this.querySelector(review.p ? 'template[data-rw-photo]' : 'template[data-rw-quote]');
    const node = template.content.firstElementChild.cloneNode(true);
    const slot = (name) => node.querySelector(`[data-slot="${name}"]`);
    const product = products[Number(review.x)] || null;
    const name = String(review.n || '').trim() || '—';
    const star = Math.max(0, Math.min(5, Number(review.s) || 0));
    const link = slot('link');
    const anchor = product && product.h === 'glowhiteningcream' ? '#glo-reviews' : '#pp-reviews';
    link.href = product ? `/products/${encodeURIComponent(product.h)}${anchor}` : '/collections/all';
    link.setAttribute('aria-label', `${name}, ${this.dataset.ratingAria.replace('[rating]', String(star))}${product ? `, ${product.n}` : ''}`);
    slot('stars').setAttribute('aria-hidden', 'true');
    slot('fill').style.width = `${star * 20}%`;
    slot('text').textContent = String(review.t || '').replace(/^["“”]+|["“”]+$/g, '').trim();
    slot('name').textContent = name;
    if (product) slot('product').textContent = product.n;
    const initial = slot('initial');
    if (initial) initial.textContent = name.charAt(0).toUpperCase();
    const verified = slot('verified');
    if (verified && Number(review.v) === 1) verified.classList.remove('hidden');
    const img = slot('img');
    if (img) img.src = `${base}${encodeURIComponent(review.p)}?width=600`;
    return node;
  }
}

if (!customElements.get('s-review-wall')) customElements.define('s-review-wall', SReviewWall);
